import { supabase } from './client'
import type { Event, PriceTier, Ticket, Guest, Organization, Staff, EventStats, RatioCheckResult, Campaign } from './database.types'

// ============================================================
// ORGANIZATIONS
// ============================================================

export async function getOrganization() {
  const { data, error } = await supabase
    .from('organizations')
    .select('*')
    .single()
  if (error) throw error
  return data as Organization
}

export async function updateOrganization(id: string, updates: Partial<Organization>) {
  const { data, error } = await supabase
    .from('organizations')
    .update(updates)
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data as Organization
}

// ============================================================
// EVENTS
// ============================================================

export async function getEvents(filters?: {
  status?: string
  from_date?: string
  to_date?: string
  limit?: number
  offset?: number
}) {
  let query = supabase
    .from('events')
    .select('*, price_tiers(*)', { count: 'exact' })
    .order('date', { ascending: true })

  if (filters?.status) query = query.eq('status', filters.status)
  if (filters?.from_date) query = query.gte('date', filters.from_date)
  if (filters?.to_date) query = query.lte('date', filters.to_date)
  if (filters?.limit) query = query.limit(filters.limit)
  if (filters?.offset) query = query.range(filters.offset, filters.offset + (filters.limit ?? 20) - 1)

  const { data, error, count } = await query
  if (error) throw error
  return { events: data as (Event & { price_tiers: PriceTier[] })[], total: count ?? 0 }
}

export async function getEvent(id: string) {
  const { data, error } = await supabase
    .from('events')
    .select('*, price_tiers(*)')
    .eq('id', id)
    .single()
  if (error) throw error
  return data as Event & { price_tiers: PriceTier[] }
}

export async function createEvent(event: Partial<Event> & { price_tiers?: Partial<PriceTier>[] }) {
  const { price_tiers, ...eventData } = event
  const { data: newEvent, error } = await supabase
    .from('events')
    .insert(eventData)
    .select()
    .single()
  if (error) throw error

  if (price_tiers?.length) {
    const tiersWithEventId = price_tiers.map(t => ({ ...t, event_id: newEvent.id }))
    const { error: tierError } = await supabase
      .from('price_tiers')
      .insert(tiersWithEventId)
    if (tierError) throw tierError
  }

  return getEvent(newEvent.id)
}

export async function updateEvent(id: string, updates: Partial<Event>) {
  const { data, error } = await supabase
    .from('events')
    .update(updates)
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data as Event
}

export async function updateEventWithTiers(
  id: string,
  updates: Partial<Event>,
  priceTiers?: Partial<PriceTier>[]
) {
  const { data, error } = await supabase
    .from('events')
    .update(updates)
    .eq('id', id)
    .select()
    .single()
  if (error) throw error

  if (priceTiers) {
    // Delete existing tiers and insert new ones
    const { error: deleteError } = await supabase
      .from('price_tiers')
      .delete()
      .eq('event_id', id)
    if (deleteError) throw deleteError

    if (priceTiers.length > 0) {
      const tiersWithEventId = priceTiers.map((t) => ({ ...t, event_id: id }))
      const { error: insertError } = await supabase
        .from('price_tiers')
        .insert(tiersWithEventId)
      if (insertError) throw insertError
    }
  }

  return getEvent(id)
}

export async function createRecurringEvents(
  parentEvent: Event & { price_tiers?: PriceTier[] },
  until: string,
  freq: 'weekly' | 'biweekly' | 'monthly' = 'weekly'
) {
  const startDate = new Date(parentEvent.date)
  const endDate = new Date(until)
  const dates: string[] = []

  if (freq === 'monthly') {
    const dayOfMonth = startDate.getDate()
    const current = new Date(startDate)
    current.setMonth(current.getMonth() + 1)
    current.setDate(dayOfMonth)
    while (current <= endDate) {
      dates.push(current.toISOString().split('T')[0])
      current.setMonth(current.getMonth() + 1)
      current.setDate(dayOfMonth)
    }
  } else {
    const increment = freq === 'weekly' ? 7 : 14
    const current = new Date(startDate)
    current.setDate(current.getDate() + increment)
    while (current <= endDate) {
      dates.push(current.toISOString().split('T')[0])
      current.setDate(current.getDate() + increment)
    }
  }

  const childEvents: Event[] = []

  for (const date of dates) {
    const { price_tiers: parentTiers, id: _parentId, created_at: _ca, updated_at: _ua, ...eventFields } = parentEvent as any
    const child = await createEvent({
      ...eventFields,
      date,
      parent_event_id: parentEvent.id,
      status: 'draft',
      published_at: null,
      price_tiers: parentTiers?.map((t: PriceTier, i: number) => ({
        name: t.name,
        guest_type: t.guest_type,
        price_cents: t.price_cents,
        capacity: t.capacity,
        description: t.description,
        early_bird_price_cents: t.early_bird_price_cents,
        early_bird_until: t.early_bird_until,
        sort_order: i,
      })),
    })
    childEvents.push(child)
  }

  return childEvents
}

export async function cancelEvent(id: string) {
  const { data, error } = await supabase
    .from('events')
    .update({ status: 'cancelled' })
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data as Event
}

export async function deleteEvent(id: string) {
  // Delete price tiers first (foreign key)
  const { error: tierError } = await supabase
    .from('price_tiers')
    .delete()
    .eq('event_id', id)
  if (tierError) throw tierError

  const { error } = await supabase
    .from('events')
    .delete()
    .eq('id', id)
  if (error) throw error
}

export async function publishEvent(id: string) {
  return updateEvent(id, { status: 'published', published_at: new Date().toISOString() } as any)
}

// ============================================================
// TICKETS
// ============================================================

export async function getTicketsForEvent(eventId: string, status?: string) {
  let query = supabase
    .from('tickets')
    .select('*, price_tiers(name, guest_type, price_cents), guests(pseudonym, tags)')
    .eq('event_id', eventId)
    .order('created_at', { ascending: false })

  if (status) query = query.eq('status', status)

  const { data, error } = await query
  if (error) throw error
  return data as (Ticket & { price_tiers: PriceTier; guests: Pick<Guest, 'pseudonym' | 'tags'> | null })[]
}

export async function checkinTicket(qrCodeHash: string, staffId: string) {
  const { data: ticket, error: findError } = await supabase
    .from('tickets')
    .select('id, status, event_id, tier_id')
    .eq('qr_code_hash', qrCodeHash)
    .single()

  if (findError || !ticket) {
    return { success: false, error: 'Ticket nicht gefunden', code: 'NOT_FOUND' } as const
  }

  if (ticket.status === 'checked_in') {
    return { success: false, error: 'Ticket bereits eingecheckt', code: 'ALREADY_USED' } as const
  }

  if (ticket.status !== 'valid') {
    return { success: false, error: `Ticket Status: ${ticket.status}`, code: 'INVALID' } as const
  }

  const { data, error } = await supabase
    .from('tickets')
    .update({
      status: 'checked_in',
      checked_in_at: new Date().toISOString(),
      checked_in_by: staffId,
    })
    .eq('id', ticket.id)
    .select('*, price_tiers(name, guest_type), guests(pseudonym)')
    .single()

  if (error) throw error

  return { success: true, ticket: data } as const
}

// ============================================================
// GUESTS
// ============================================================

export async function getGuests(filters?: {
  search?: string
  tags?: string[]
  guest_type?: string
  limit?: number
  offset?: number
}) {
  let query = supabase
    .from('guests')
    .select('*', { count: 'exact' })
    .order('last_visit', { ascending: false, nullsFirst: false })

  if (filters?.search) {
    query = query.ilike('pseudonym', `%${filters.search}%`)
  }
  if (filters?.tags?.length) {
    query = query.overlaps('tags', filters.tags)
  }
  if (filters?.guest_type) {
    query = query.eq('guest_type', filters.guest_type)
  }
  if (filters?.limit) query = query.limit(filters.limit)
  if (filters?.offset) query = query.range(filters.offset, filters.offset + (filters.limit ?? 50) - 1)

  const { data, error, count } = await query
  if (error) throw error
  return { guests: data as Guest[], total: count ?? 0 }
}

export async function getGuest(guestId: string) {
  const { data, error } = await supabase
    .from('guests')
    .select('*')
    .eq('id', guestId)
    .single()
  if (error) throw error
  return data as Guest
}

export async function updateGuestTags(guestId: string, tags: string[]) {
  const { data, error } = await supabase
    .from('guests')
    .update({ tags })
    .eq('id', guestId)
    .select()
    .single()
  if (error) throw error
  return data as Guest
}

// ============================================================
// STATS
// ============================================================

export async function getEventStats(eventId: string): Promise<EventStats> {
  const { data, error } = await supabase.rpc('get_event_stats', { p_event_id: eventId })
  if (error) throw error
  return data as EventStats
}

export async function checkRatioLimit(eventId: string, guestType: string, count = 1): Promise<RatioCheckResult> {
  const { data, error } = await supabase.rpc('check_ratio_limit', {
    p_event_id: eventId,
    p_guest_type: guestType,
    p_count: count,
  })
  if (error) throw error
  return data as RatioCheckResult
}

// ============================================================
// PAYMENTS EXPORT
// ============================================================

export async function getPaymentsForExport(filters?: { from?: string; to?: string }) {
  let query = supabase
    .from('payments')
    .select('*, tickets(ticket_number, event_id, events(title, date))')
    .order('created_at', { ascending: false })

  if (filters?.from) query = query.gte('created_at', filters.from)
  if (filters?.to) query = query.lte('created_at', filters.to)

  return query
}

// ============================================================
// STAFF
// ============================================================

export async function getStaff() {
  const { data, error } = await supabase
    .from('staff')
    .select('*')
    .order('role')
  if (error) throw error
  return data as Staff[]
}

export async function updateStaff(id: string, updates: Partial<Staff>) {
  const { data, error } = await supabase
    .from('staff')
    .update(updates)
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data as Staff
}

// ============================================================
// REALTIME SUBSCRIPTIONS
// ============================================================

export function subscribeToTickets(eventId: string, callback: (ticket: Ticket) => void) {
  return supabase
    .channel(`tickets:${eventId}`)
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'tickets',
      filter: `event_id=eq.${eventId}`,
    }, (payload) => {
      callback(payload.new as Ticket)
    })
    .subscribe()
}

// ============================================================
// CAMPAIGNS
// ============================================================

export async function getCampaigns(filters?: { status?: string }) {
  let query = supabase
    .from('campaigns')
    .select('*')
    .order('created_at', { ascending: false })

  if (filters?.status) query = query.eq('status', filters.status)

  const { data, error } = await query
  if (error) throw error
  return data as Campaign[]
}

export async function getCampaign(id: string) {
  const { data, error } = await supabase
    .from('campaigns')
    .select('*')
    .eq('id', id)
    .single()
  if (error) throw error
  return data as Campaign
}

export async function createCampaign(data: {
  subject: string
  body_html: string
  segment_filter?: { tags?: string[]; min_visits?: number } | null
}) {
  const { data: campaign, error } = await supabase
    .from('campaigns')
    .insert({ ...data, status: 'draft' })
    .select()
    .single()
  if (error) throw error
  return campaign as Campaign
}

export async function updateCampaign(id: string, updates: Partial<Campaign>) {
  const { data, error } = await supabase
    .from('campaigns')
    .update(updates)
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data as Campaign
}

export async function deleteCampaign(id: string) {
  const { error } = await supabase
    .from('campaigns')
    .delete()
    .eq('id', id)
  if (error) throw error
}

export async function getNewsletterRecipientCount(segmentFilter?: {
  tags?: string[]
  min_visits?: number
}) {
  let query = supabase
    .from('guests')
    .select('id', { count: 'exact', head: true })
    .eq('newsletter_opt_in', true)

  if (segmentFilter?.tags?.length) {
    query = query.overlaps('tags', segmentFilter.tags)
  }
  if (segmentFilter?.min_visits) {
    query = query.gte('visit_count', segmentFilter.min_visits)
  }

  const { count, error } = await query
  if (error) throw error
  return count ?? 0
}
